import { AlertTriangle, ArrowRight, Circle, GitBranch, Link as LinkIcon, OctagonX, RefreshCw, Unlink } from 'lucide-react'
import { formatEnteredTrackFeet } from '../utils/formatEnteredTrackFeet'
import type { SwitchList, SwitchListItem } from '../types/interchange'

/**
 * Shared switch list preview: metrics grid + per-task micro-step list.
 * Extracted verbatim from DispatchGatewayModal so the Switch Job Builder
 * and the EDI dispatch modal render identically (Plan 24 §5.1).
 */
export function SwitchListPreview({
  switchList,
  loading,
}: {
  switchList?: SwitchList | null
  loading: boolean
}) {
  return (
    <div className="space-y-4">
      {loading ? (
        <div className="text-muted-foreground">Loading switch list...</div>
      ) : !switchList || !switchList.items || switchList.items.length === 0 ? (
        <div className="text-muted-foreground">No switch list generated</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <div className="bg-muted/50 border border-border rounded-md p-2">
              <div className="text-muted-foreground text-xs">Throws</div>
              <div className="font-semibold">{switchList.switchThrowCount ?? 0}</div>
            </div>
            <div className="bg-muted/50 border border-border rounded-md p-2">
              <div className="text-muted-foreground text-xs">Couplings</div>
              <div className="font-semibold">{switchList.couplingCount ?? 0}</div>
            </div>
            <div className="bg-muted/50 border border-border rounded-md p-2">
              <div className="text-muted-foreground text-xs">Reversals</div>
              <div className="font-semibold">{switchList.reversalCount ?? 0}</div>
            </div>
            <div className="bg-muted/50 border border-border rounded-md p-2">
              <div className="text-muted-foreground text-xs">Distance</div>
              <div className="font-semibold">{switchList.totalDistanceFeet ?? 0} ft</div>
            </div>
            <div className="bg-muted/50 border border-border rounded-md p-2">
              <div className="text-muted-foreground text-xs">Est. Time</div>
              <div className="font-semibold">{switchList.estimatedTimeToCompleteMinutes ?? 0} min</div>
            </div>
            <div className="bg-muted/50 border border-border rounded-md p-2">
              <div className="text-muted-foreground text-xs">Fuel</div>
              <div className="font-semibold">{switchList.estimatedFuelBurnGallons ?? 0} gal</div>
            </div>
          </div>

          <div className="space-y-2 max-h-[240px] overflow-y-auto">
            {switchList.items.map((item: SwitchListItem) => (
              <div key={item.taskId} className="rounded-md border border-border bg-muted/50 p-3">
                <div className={`font-semibold text-sm pb-2 mb-2 ${item.status === 'BLOCKED' ? 'border-b border-destructive/40' : 'border-b border-border'}`}>
                  {item.passSummary ?? item.summary}
                </div>
                {item.status === 'BLOCKED' && item.statusReason && (
                  <div className="flex items-start gap-1.5 text-xs text-destructive mb-1">
                    <OctagonX className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>{item.statusReason}</span>
                  </div>
                )}
                {(item.warnings ?? []).map((warning, wi) => (
                  <div
                    key={`${item.taskId}-warn-${wi}`}
                    className="flex items-start gap-1.5 text-xs mb-1 rounded-md border border-amber-500/50 bg-amber-500/10 px-2 py-1 text-amber-500"
                  >
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span className="flex-1">{warning}</span>
                    <span className="font-mono text-[10px] uppercase tracking-wide opacity-70 mt-0.5 shrink-0">WARN</span>
                  </div>
                ))}
                {item.targetSpotIdentifier && (
                  <div className="text-xs text-muted-foreground mb-1">
                    Spot: {item.targetSpotIdentifier}
                    {item.targetDoorNumber ? ` · Door ${item.targetDoorNumber}` : ''}
                  </div>
                )}
                {item.microSteps.length > 0 && (
                  <ul className="pl-0 list-none text-xs text-muted-foreground space-y-1">
                    {item.microSteps.map((step, i) => (
                      <li key={`${item.taskId}-step-${i}`} className="flex items-start gap-2">
                        <StepIcon type={step.type} className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span className="flex-1">{step.description ?? step.type}</span>
                        {step.switchName && (
                          <span className="text-xs text-muted-foreground">{step.switchName}</span>
                        )}
                        {step.travelDistanceFeet !== undefined && (
                          <span className="text-xs text-muted-foreground">{formatEnteredTrackFeet(step.travelDistanceFeet)}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function StepIcon({ type, className }: { type: string; className?: string }) {
  const props = { className }
  switch (type) {
    case 'THROW_SWITCH':
      return <GitBranch {...props} />
    case 'TRAVEL':
      return <ArrowRight {...props} />
    case 'COUPLE':
      return <LinkIcon {...props} />
    case 'UNCOUPLE':
      return <Unlink {...props} />
    case 'REVERSE':
      return <RefreshCw {...props} />
    default:
      return <Circle {...props} />
  }
}
